/**
 * CECL Pools — Pool admin page (Phase 16 Track B).
 *
 * Full CRUD over contract_asset_pools.
 * ASC 326-20 Current Expected Credit Losses on contract assets.
 *
 * Backend endpoints (shipped 2026-04-20):
 *   GET    /api/v1/revenue/cecl/pools
 *   POST   /api/v1/revenue/cecl/pools
 *   PATCH  /api/v1/revenue/cecl/pools/{id}
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import { useApp } from '../contexts/AppContext';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import Modal from '../components/ui/Modal';

const REV_API = `${API_V1}/revenue`;

// ── Types ──────────────────────────────────────────────────────

type Methodology = 'LOSS_RATE' | 'WARM';
type ReversionMethod = 'STRAIGHT_LINE' | 'IMMEDIATE' | 'NONE';

interface QFactorDriver {
  driver_name: string;
  driver_value: number; // decimal, e.g. 0.0025 = 0.25%
  rationale_text: string;
  source_citation: string;
  effective_periods: number;
}

interface Pool {
  id: number;
  pool_code: string;
  pool_name: string;
  methodology: Methodology;
  credit_tier: string | null;
  duration_bucket: string | null;
  industry_code: string | null;
  geography_code: string | null;
  base_loss_rate: number;
  q_factor_drivers: QFactorDriver[];
  q_factor_reversion_method: ReversionMethod;
  q_factor_reversion_periods: number | null;
  methodology_rationale: string | null;
  is_active: boolean;
  created_at: string | null;
  created_by: number | null;
  updated_at: string | null;
  contracts_assigned_count?: number;
  total_exposure?: number;
}

interface PoolsResponse {
  count: number;
  pools: Pool[];
}

// ── Dropdown constants ─────────────────────────────────────────

const METHODOLOGY_OPTIONS: { value: Methodology; label: string }[] = [
  { value: 'LOSS_RATE', label: 'LOSS_RATE — historical loss rate' },
  { value: 'WARM', label: 'WARM — weighted-average remaining maturity' },
];

const CREDIT_TIER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '(none — matches all)' },
  { value: 'HIGH', label: 'HIGH' },
  { value: 'MEDIUM', label: 'MEDIUM' },
  { value: 'LOW', label: 'LOW' },
  { value: 'SUBPRIME', label: 'SUBPRIME' },
  { value: 'UNKNOWN', label: 'UNKNOWN' },
];

const DURATION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '(none)' },
  { value: 'LESS_THAN_1Y', label: 'LESS_THAN_1Y' },
  { value: '1_TO_3Y', label: '1_TO_3Y' },
  { value: '3_TO_5Y', label: '3_TO_5Y' },
  { value: 'GT_5Y', label: 'GT_5Y' },
];

const REVERSION_OPTIONS: { value: ReversionMethod; label: string }[] = [
  { value: 'STRAIGHT_LINE', label: 'STRAIGHT_LINE' },
  { value: 'IMMEDIATE', label: 'IMMEDIATE' },
  { value: 'NONE', label: 'NONE' },
];

// ── Form state ─────────────────────────────────────────────────

interface PoolFormState {
  pool_code: string;
  pool_name: string;
  methodology: Methodology;
  credit_tier: string;
  duration_bucket: string;
  industry_code: string;
  geography_code: string;
  base_loss_rate_pct: string; // user enters %, we convert to decimal
  q_factor_drivers: Array<{
    driver_name: string;
    driver_value_pct: string;
    rationale_text: string;
    source_citation: string;
    effective_periods: string;
  }>;
  q_factor_reversion_method: ReversionMethod;
  q_factor_reversion_periods: string;
  methodology_rationale: string;
  change_rationale_text: string;
}

const blankForm = (): PoolFormState => ({
  pool_code: '',
  pool_name: '',
  methodology: 'LOSS_RATE',
  credit_tier: '',
  duration_bucket: '',
  industry_code: '',
  geography_code: '',
  base_loss_rate_pct: '',
  q_factor_drivers: [],
  q_factor_reversion_method: 'STRAIGHT_LINE',
  q_factor_reversion_periods: '8',
  methodology_rationale: '',
  change_rationale_text: '',
});

const poolToForm = (p: Pool): PoolFormState => ({
  pool_code: p.pool_code,
  pool_name: p.pool_name,
  methodology: p.methodology,
  credit_tier: p.credit_tier || '',
  duration_bucket: p.duration_bucket || '',
  industry_code: p.industry_code || '',
  geography_code: p.geography_code || '',
  base_loss_rate_pct: ((p.base_loss_rate || 0) * 100).toFixed(4),
  q_factor_drivers: (p.q_factor_drivers || []).map(d => ({
    driver_name: d.driver_name,
    driver_value_pct: ((d.driver_value || 0) * 100).toFixed(4),
    rationale_text: d.rationale_text || '',
    source_citation: d.source_citation || '',
    effective_periods: String(d.effective_periods || 0),
  })),
  q_factor_reversion_method: p.q_factor_reversion_method || 'STRAIGHT_LINE',
  q_factor_reversion_periods: String(p.q_factor_reversion_periods || 8),
  methodology_rationale: p.methodology_rationale || '',
  change_rationale_text: '',
});

// ── Helpers ────────────────────────────────────────────────────

const pctToDecimal = (s: string): number => {
  const n = parseFloat(s);
  if (!isFinite(n)) return 0;
  return n / 100;
};

const decimalToPct = (d: number | null | undefined, places = 4): string => {
  if (d == null || !isFinite(d)) return '—';
  return `${(d * 100).toFixed(places)}%`;
};

// ── Main component ─────────────────────────────────────────────

export default function CECLPools() {
  const { entity } = useGlobalEntity();
  const { authUser } = useApp();
  const [pools, setPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ceclEnabled, setCeclEnabled] = useState<boolean | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editingPool, setEditingPool] = useState<Pool | null>(null);
  const [form, setForm] = useState<PoolFormState>(blankForm());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Confirm deactivation state
  const [deactivatePool, setDeactivatePool] = useState<Pool | null>(null);

  const currentUserId = authUser?.id ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = withEntityParam(
        `${REV_API}/cecl/pools?active_only=${showInactive ? 'false' : 'true'}`,
        entity,
      );
      const r = await authFetch(url);
      if (r.ok) {
        const data: PoolsResponse = await r.json();
        setPools(data.pools || []);
      } else {
        const err = await r.json().catch(() => ({}));
        setError(err.detail || `HTTP ${r.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    }
    setLoading(false);
  }, [entity, showInactive]);

  const loadConfigState = useCallback(async () => {
    try {
      const r = await authFetch(
        withEntityParam(`${API_V1}/org-config/USE_CECL_FOR_CONTRACT_ASSETS`, entity),
      );
      if (r.ok) {
        const d = await r.json();
        // Accept either { value: 'true' } or { enabled: true } shape
        const raw = d?.value ?? d?.enabled ?? d?.config_value;
        if (typeof raw === 'boolean') setCeclEnabled(raw);
        else if (typeof raw === 'string') setCeclEnabled(raw.toLowerCase() === 'true');
        else setCeclEnabled(null);
      }
    } catch { /* endpoint optional; banner just won't show */ }
  }, [entity]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadConfigState(); }, [loadConfigState]);

  // ── KPIs ──────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const total = pools.length;
    const lossRate = pools.filter(p => p.methodology === 'LOSS_RATE').length;
    const warm = pools.filter(p => p.methodology === 'WARM').length;
    const assigned = pools.reduce((s, p) => s + (p.contracts_assigned_count || 0), 0);
    const exposure = pools.reduce((s, p) => s + (p.total_exposure || 0), 0);
    return { total, lossRate, warm, assigned, exposure };
  }, [pools]);

  // ── Form handlers ─────────────────────────────────────────────

  const openNew = () => {
    setEditingPool(null);
    setForm(blankForm());
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (p: Pool) => {
    setEditingPool(p);
    setForm(poolToForm(p));
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingPool(null);
    setForm(blankForm());
    setFormError(null);
  };

  const addDriver = () => {
    setForm(f => ({
      ...f,
      q_factor_drivers: [
        ...f.q_factor_drivers,
        { driver_name: '', driver_value_pct: '0', rationale_text: '', source_citation: '', effective_periods: '4' },
      ],
    }));
  };

  const removeDriver = (idx: number) => {
    setForm(f => ({
      ...f,
      q_factor_drivers: f.q_factor_drivers.filter((_, i) => i !== idx),
    }));
  };

  const updateDriver = (idx: number, key: string, value: string) => {
    setForm(f => ({
      ...f,
      q_factor_drivers: f.q_factor_drivers.map((d, i) =>
        i === idx ? { ...d, [key]: value } : d,
      ),
    }));
  };

  const buildPayload = () => {
    const drivers: QFactorDriver[] = form.q_factor_drivers.map(d => ({
      driver_name: d.driver_name,
      driver_value: pctToDecimal(d.driver_value_pct),
      rationale_text: d.rationale_text,
      source_citation: d.source_citation,
      effective_periods: parseInt(d.effective_periods, 10) || 0,
    }));
    const body: any = {
      pool_code: form.pool_code,
      pool_name: form.pool_name,
      methodology: form.methodology,
      credit_tier: form.credit_tier || null,
      duration_bucket: form.duration_bucket || null,
      industry_code: form.industry_code || null,
      geography_code: form.geography_code || null,
      base_loss_rate: pctToDecimal(form.base_loss_rate_pct),
      q_factor_drivers: drivers,
      q_factor_reversion_method: form.q_factor_reversion_method,
      q_factor_reversion_periods: form.q_factor_reversion_method === 'STRAIGHT_LINE'
        ? parseInt(form.q_factor_reversion_periods, 10) || null
        : null,
      methodology_rationale: form.methodology_rationale,
    };
    return body;
  };

  // Detect whether edit triggers SOD/rationale-required path
  const editTriggersChangeLog = useMemo(() => {
    if (!editingPool) return false;
    const curDrivers = JSON.stringify((editingPool.q_factor_drivers || []).map(d => ({
      driver_name: d.driver_name, driver_value: d.driver_value,
      rationale_text: d.rationale_text, source_citation: d.source_citation,
      effective_periods: d.effective_periods,
    })));
    const newDrivers = JSON.stringify(form.q_factor_drivers.map(d => ({
      driver_name: d.driver_name,
      driver_value: pctToDecimal(d.driver_value_pct),
      rationale_text: d.rationale_text,
      source_citation: d.source_citation,
      effective_periods: parseInt(d.effective_periods, 10) || 0,
    })));
    const rateChanged = Math.abs(pctToDecimal(form.base_loss_rate_pct) - (editingPool.base_loss_rate || 0)) > 1e-9;
    const methodChanged = editingPool.methodology !== form.methodology;
    return rateChanged || methodChanged || curDrivers !== newDrivers;
  }, [editingPool, form]);

  const sodViolation = editingPool && currentUserId != null && editingPool.created_by === currentUserId;

  const submit = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      // Client-side validation
      if (!form.pool_code.trim() || !form.pool_name.trim()) {
        setFormError('pool_code and pool_name are required');
        setSubmitting(false);
        return;
      }
      if (!['LOSS_RATE', 'WARM'].includes(form.methodology)) {
        setFormError('methodology must be LOSS_RATE or WARM');
        setSubmitting(false);
        return;
      }
      if (form.methodology_rationale.length < 60) {
        setFormError(`methodology_rationale must be >= 60 chars (currently ${form.methodology_rationale.length})`);
        setSubmitting(false);
        return;
      }
      if (editingPool && editTriggersChangeLog && form.change_rationale_text.length < 60) {
        setFormError(`change_rationale_text must be >= 60 chars when modifying base_loss_rate, q_factor_drivers, or methodology (currently ${form.change_rationale_text.length})`);
        setSubmitting(false);
        return;
      }

      const body = buildPayload();
      if (editingPool && editTriggersChangeLog) {
        body.change_rationale_text = form.change_rationale_text;
      }

      let url: string;
      let method: string;
      if (editingPool) {
        url = `${REV_API}/cecl/pools/${editingPool.id}`;
        method = 'PATCH';
      } else {
        url = `${REV_API}/cecl/pools`;
        method = 'POST';
      }

      const r = await authFetch(withEntityParam(url, entity), {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (r.ok) {
        closeForm();
        await load();
      } else {
        const err = await r.json().catch(() => ({}));
        const code = err?.error_code || err?.code;
        const detail = err?.detail || err?.message || `HTTP ${r.status}`;
        if (code === 'CECL_BACKFILL_INCOMPLETE') {
          setFormError('CECL_BACKFILL_INCOMPLETE — run backfill before enabling.');
        } else {
          setFormError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }
      }
    } catch (e: any) {
      setFormError(e?.message || 'Network error');
    }
    setSubmitting(false);
  };

  // ── Deactivation ──────────────────────────────────────────────

  const confirmDeactivate = async () => {
    if (!deactivatePool) return;
    setSubmitting(true);
    try {
      const r = await authFetch(withEntityParam(`${REV_API}/cecl/pools/${deactivatePool.id}`, entity), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_active: false,
          change_rationale_text: `Deactivating pool ${deactivatePool.pool_code} — admin action via CECL Pools page. Pool no longer used for new contract assignments.`,
        }),
      });
      if (r.ok) {
        setDeactivatePool(null);
        await load();
      } else {
        const err = await r.json().catch(() => ({}));
        setError(err.detail || `HTTP ${r.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    }
    setSubmitting(false);
  };

  // ── Columns ───────────────────────────────────────────────────

  const cols: Column[] = [
    { key: 'pool_code', label: 'Code', render: (v: string) => <span style={{ fontWeight: 700, fontSize: 12 }}>{v}</span> },
    { key: 'pool_name', label: 'Name' },
    {
      key: 'methodology', label: 'Method',
      render: (v: string) => (
        <span style={{
          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
          background: v === 'WARM' ? 'rgba(139,92,246,0.12)' : 'rgba(59,130,246,0.12)',
          color: v === 'WARM' ? '#7c3aed' : '#2563eb',
        }}>{v}</span>
      ),
    },
    { key: 'credit_tier', label: 'Tier', render: (v: string | null) => v || '—' },
    { key: 'duration_bucket', label: 'Duration', render: (v: string | null) => v || '—' },
    { key: 'industry_code', label: 'Industry', render: (v: string | null) => v || '—' },
    { key: 'geography_code', label: 'Geo', render: (v: string | null) => v || '—' },
    {
      key: 'base_loss_rate', label: 'Base Rate', align: 'right',
      render: (v: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{decimalToPct(v, 4)}</span>,
    },
    {
      key: 'contracts_assigned_count', label: '# Contracts', align: 'right',
      render: (v: number | undefined) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v ?? 0}</span>,
    },
    {
      key: '_actions', label: '', align: 'right',
      render: (_: any, row: Pool) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <ActionButton label="Edit" size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); openEdit(row); }} />
          {row.is_active && (
            <ActionButton
              label="Deactivate"
              size="sm"
              variant="danger"
              onClick={(e) => { e.stopPropagation(); setDeactivatePool(row); }}
            />
          )}
        </div>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────

  const driverSumDecimal = form.q_factor_drivers.reduce(
    (s, d) => s + pctToDecimal(d.driver_value_pct), 0,
  );

  return (
    <PageLayout
      title="CECL Pools"
      subtitle="ASC 326-20 — Contract asset pools, methodology, and Q-factor overlays"
      action={{ label: '+ New Pool', onClick: openNew, variant: 'primary' }}
    >
      {/* Inert banner */}
      {ceclEnabled === false && (
        <div style={{
          padding: '12px 16px', marginBottom: 16, borderRadius: 6,
          background: 'rgba(245,158,11,0.10)',
          border: '1px solid rgba(245,158,11,0.30)',
          fontSize: 13, color: 'var(--text-primary)',
        }}>
          <strong>CECL engine currently inert.</strong> Configure pools now; an admin must enable via <em>CECL Admin</em> before allowances post.
        </div>
      )}

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 6,
          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
          color: 'var(--accent-critical)', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      <KpiGrid columns={5} items={[
        { label: 'Total Pools', value: kpis.total },
        { label: 'LOSS_RATE', value: kpis.lossRate, color: '#2563eb' },
        { label: 'WARM', value: kpis.warm, color: '#7c3aed' },
        { label: 'Contracts Assigned', value: kpis.assigned },
        { label: 'CECL-Eligible Exposure', value: formatAmountCompact(kpis.exposure), color: 'var(--accent)' },
      ]} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      <DataTable
        columns={cols}
        rows={pools}
        loading={loading}
        rowKey="id"
        emptyMessage="No CECL pools configured. Click + New Pool to create one."
      />

      {/* Create/Edit form modal */}
      <Modal
        open={formOpen}
        onClose={closeForm}
        size="lg"
        title={editingPool ? `Edit Pool: ${editingPool.pool_code}` : 'New CECL Pool'}
        description={editingPool
          ? 'Modifications to base rate, drivers, or methodology require a >= 60-char change rationale. Changes are logged to pool_parameter_change_log.'
          : 'ASC 326-20 pool definition. Methodology rationale (>= 60 chars) is required.'
        }
        footer={
          <>
            <ActionButton label="Cancel" variant="secondary" onClick={closeForm} disabled={submitting} />
            <ActionButton
              label={submitting ? 'Saving...' : (editingPool ? 'Save Changes' : 'Create Pool')}
              variant="primary"
              onClick={submit}
              disabled={submitting}
            />
          </>
        }
      >
        {sodViolation && (
          <div style={{
            padding: '10px 14px', marginBottom: 12, borderRadius: 6,
            background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
            color: 'var(--accent-critical)', fontSize: 12,
          }}>
            <strong>SOD violation:</strong> you created this pool. The backend will reject your edit — request another reviewer to edit material fields.
          </div>
        )}

        {formError && (
          <div style={{
            padding: '10px 14px', marginBottom: 12, borderRadius: 6,
            background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
            color: 'var(--accent-critical)', fontSize: 12, whiteSpace: 'pre-wrap',
          }}>
            {formError}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <Field label="Pool Code *">
            <input
              type="text"
              value={form.pool_code}
              onChange={(e) => setForm(f => ({ ...f, pool_code: e.target.value }))}
              placeholder="e.g. POOL_HIGH_1Y_TECH"
              style={inputStyle}
            />
          </Field>
          <Field label="Pool Name *">
            <input
              type="text"
              value={form.pool_name}
              onChange={(e) => setForm(f => ({ ...f, pool_name: e.target.value }))}
              placeholder="e.g. High credit tier, < 1y, Tech"
              style={inputStyle}
            />
          </Field>
        </div>

        <Field label="Methodology *">
          <div style={{ display: 'flex', gap: 12 }}>
            {METHODOLOGY_OPTIONS.map(o => (
              <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="radio"
                  name="methodology"
                  value={o.value}
                  checked={form.methodology === o.value}
                  onChange={() => setForm(f => ({ ...f, methodology: o.value }))}
                />
                {o.label}
              </label>
            ))}
          </div>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <Field label="Credit Tier">
            <select
              value={form.credit_tier}
              onChange={(e) => setForm(f => ({ ...f, credit_tier: e.target.value }))}
              style={inputStyle}
            >
              {CREDIT_TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Duration Bucket">
            <select
              value={form.duration_bucket}
              onChange={(e) => setForm(f => ({ ...f, duration_bucket: e.target.value }))}
              style={inputStyle}
            >
              {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <Field label="Industry Code (2-digit NAICS recommended)">
            <input
              type="text"
              value={form.industry_code}
              onChange={(e) => setForm(f => ({ ...f, industry_code: e.target.value }))}
              placeholder="e.g. 54"
              style={inputStyle}
            />
          </Field>
          <Field label="Geography Code (country/region)">
            <input
              type="text"
              value={form.geography_code}
              onChange={(e) => setForm(f => ({ ...f, geography_code: e.target.value }))}
              placeholder="e.g. US"
              style={inputStyle}
            />
          </Field>
        </div>

        <Field label="Base Loss Rate (%, 4 decimal precision) *">
          <input
            type="number"
            step="0.0001"
            min="0"
            max="100"
            value={form.base_loss_rate_pct}
            onChange={(e) => setForm(f => ({ ...f, base_loss_rate_pct: e.target.value }))}
            placeholder="e.g. 1.2000 for 1.20%"
            style={inputStyle}
          />
          <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
            Stored as decimal. Entered {form.base_loss_rate_pct || '0'}% → {pctToDecimal(form.base_loss_rate_pct).toFixed(6)}
          </div>
        </Field>

        {/* Q-factor drivers editor */}
        <div style={{
          marginTop: 16, marginBottom: 16, padding: 12,
          border: '1px solid var(--border-subtle)', borderRadius: 6,
          background: 'var(--bg-card)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Q-Factor Drivers ({form.q_factor_drivers.length})</div>
            <ActionButton label="+ Add Driver" size="sm" variant="secondary" onClick={addDriver} />
          </div>
          {form.q_factor_drivers.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-hint)', padding: '8px 0' }}>
              No Q-factor overlays. Add one for each forward-looking adjustment (e.g. macroeconomic, credit mix shift, industry-specific).
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {form.q_factor_drivers.map((d, i) => (
                <div key={i} style={{
                  padding: 10, border: '1px solid var(--border-subtle)', borderRadius: 4,
                  background: 'var(--bg-deep)',
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 6 }}>
                    <input
                      type="text"
                      placeholder="Driver name"
                      value={d.driver_name}
                      onChange={(e) => updateDriver(i, 'driver_name', e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      type="number"
                      step="0.0001"
                      placeholder="Value %"
                      value={d.driver_value_pct}
                      onChange={(e) => updateDriver(i, 'driver_value_pct', e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      type="number"
                      placeholder="Periods"
                      value={d.effective_periods}
                      onChange={(e) => updateDriver(i, 'effective_periods', e.target.value)}
                      style={inputStyle}
                    />
                    <ActionButton label="Remove" size="sm" variant="danger" onClick={() => removeDriver(i)} />
                  </div>
                  <textarea
                    placeholder="Rationale"
                    value={d.rationale_text}
                    onChange={(e) => updateDriver(i, 'rationale_text', e.target.value)}
                    rows={2}
                    style={{ ...inputStyle, width: '100%', marginBottom: 6 }}
                  />
                  <input
                    type="text"
                    placeholder="Source citation (e.g. Fed FOMC Mar 2026, BEA GDP Q4-25)"
                    value={d.source_citation}
                    onChange={(e) => updateDriver(i, 'source_citation', e.target.value)}
                    style={{ ...inputStyle, width: '100%' }}
                  />
                </div>
              ))}
              <div style={{
                padding: '8px 10px', borderTop: '1px dashed var(--border-subtle)',
                fontSize: 12, color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between',
              }}>
                <span>Live sum of driver values:</span>
                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{decimalToPct(driverSumDecimal, 4)}</strong>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <Field label="Q-Factor Reversion Method">
            <select
              value={form.q_factor_reversion_method}
              onChange={(e) => setForm(f => ({ ...f, q_factor_reversion_method: e.target.value as ReversionMethod }))}
              style={inputStyle}
            >
              {REVERSION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          {form.q_factor_reversion_method === 'STRAIGHT_LINE' && (
            <Field label="Reversion Periods">
              <input
                type="number"
                min="1"
                value={form.q_factor_reversion_periods}
                onChange={(e) => setForm(f => ({ ...f, q_factor_reversion_periods: e.target.value }))}
                style={inputStyle}
              />
            </Field>
          )}
        </div>

        <Field label={`Methodology Rationale (>= 60 chars, currently ${form.methodology_rationale.length}) *`}>
          <textarea
            rows={3}
            value={form.methodology_rationale}
            onChange={(e) => setForm(f => ({ ...f, methodology_rationale: e.target.value }))}
            placeholder="Cite why this methodology fits the pool's risk characteristics per ASC 326-20-30-5..."
            style={{ ...inputStyle, width: '100%' }}
          />
          <div style={{ fontSize: 11, color: form.methodology_rationale.length < 60 ? 'var(--accent-critical)' : 'var(--positive)', marginTop: 4 }}>
            {form.methodology_rationale.length < 60
              ? `${60 - form.methodology_rationale.length} more character(s) required`
              : 'OK'}
          </div>
        </Field>

        {editingPool && editTriggersChangeLog && (
          <div style={{
            marginTop: 12, padding: 12,
            border: '1px solid rgba(245,158,11,0.30)', borderRadius: 6,
            background: 'rgba(245,158,11,0.08)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: 'var(--warning)' }}>
              Material change detected — change rationale required (written to pool_parameter_change_log).
            </div>
            <textarea
              rows={3}
              value={form.change_rationale_text}
              onChange={(e) => setForm(f => ({ ...f, change_rationale_text: e.target.value }))}
              placeholder="Explain why base_loss_rate, drivers, or methodology is being modified (>= 60 chars)..."
              style={{ ...inputStyle, width: '100%' }}
            />
            <div style={{ fontSize: 11, color: form.change_rationale_text.length < 60 ? 'var(--accent-critical)' : 'var(--positive)', marginTop: 4 }}>
              {form.change_rationale_text.length < 60
                ? `${60 - form.change_rationale_text.length} more character(s) required`
                : 'OK'}
            </div>
          </div>
        )}
      </Modal>

      {/* Deactivate confirmation */}
      <Modal
        open={!!deactivatePool}
        onClose={() => setDeactivatePool(null)}
        size="sm"
        title="Deactivate Pool"
        description={deactivatePool ? `Pool ${deactivatePool.pool_code} will no longer accept new contract assignments.` : ''}
        footer={
          <>
            <ActionButton label="Cancel" variant="secondary" onClick={() => setDeactivatePool(null)} disabled={submitting} />
            <ActionButton label={submitting ? 'Deactivating...' : 'Deactivate'} variant="danger" onClick={confirmDeactivate} disabled={submitting} />
          </>
        }
      >
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          Existing contract assignments to this pool are preserved. The pool is flagged inactive and hidden from the assignment dropdown.
          Contracts already assigned continue to recompute allowances until re-assigned elsewhere.
        </div>
      </Modal>
    </PageLayout>
  );
}

// ── Small UI helpers ──────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 4,
  border: '1px solid var(--border-subtle)',
  background: 'var(--bg-card)', color: 'var(--text-primary)',
  fontSize: 13, fontFamily: 'var(--font-sans)',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-hint)', letterSpacing: '0.04em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}
